// cspell:ignore PKCE
import { isStytchProdEnv } from 'utils/user';

/**
 * Native (iOS/Android Capacitor wrapper) Google OAuth.
 *
 * Google blocks OAuth inside the embedded WKWebView (403 disallowed_useragent),
 * so on native we run the Google step in a system browser the OS controls
 * (ASWebAuthenticationSession on iOS) and let the OS hand the result back to the
 * app via a custom callback scheme. That OS-level interception is what
 * guarantees the user lands back in the app, not stranded in the browser.
 *
 * Flow:
 *  1. Open Stytch's HTTPS OAuth start URL in the auth session (redirect ->
 *     /oauth-callback.html, a static page that bounces to the custom scheme).
 *  2. The OS intercepts `com.getthera.app://oauth?...token=...`, auto-dismisses
 *     the browser, and resolves the plugin with that URL.
 *  3. Caller routes the WebView to /authenticate?token=... which runs the
 *     existing stytch.oauth.authenticate(token) -> session lands in the WebView.
 *
 * PKCE: we keep an HTTPS redirect, so Stytch does not force PKCE; authenticate()
 * needs no client-stored code_verifier (same as the magic-link flow).
 */

export const NATIVE_OAUTH_SCHEME = 'com.getthera.app';

export const isNativeApp = (): boolean =>
  typeof window !== 'undefined' &&
  Boolean((window as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor?.isNativePlatform?.());

interface AuthSessionPlugin {
  start(options: { url: string; callbackScheme: string }): Promise<{ callbackUrl: string }>;
}

/**
 * The native shell registers a `TheraOAuth` plugin (ASWebAuthenticationSession
 * on iOS). Returns null if the bridge/plugin isn't present (e.g. plain web), so
 * callers can fall back to the web redirect flow.
 */
const getAuthSessionPlugin = (): AuthSessionPlugin | null => {
  if (typeof window === 'undefined') return null;
  const plugins = (window as { Capacitor?: { Plugins?: { TheraOAuth?: AuthSessionPlugin } } }).Capacitor?.Plugins;
  return plugins?.TheraOAuth ?? null;
};

const getStytchApiBase = (): string => (isStytchProdEnv ? 'https://api.stytch.com' : 'https://test.stytch.com');

export interface NativeOAuthResult {
  token: string;
  stytchTokenType: string;
}

/**
 * Runs the native Google OAuth session and returns the Stytch token to exchange.
 * Returns null if not runnable (no plugin/token) so the caller can fall back.
 * Throws only on an actual session error the caller should log.
 */
export async function startNativeGoogleOAuth(): Promise<NativeOAuthResult | null> {
  const plugin = getAuthSessionPlugin();
  const publicToken = process.env.NEXT_PUBLIC_STYTCH_PUBLIC_TOKEN;
  if (!plugin || !publicToken) return null;

  const origin = window.location.origin;
  // HTTPS redirect to a static bounce page keeps Stytch's PKCE off and avoids
  // loading the whole app inside the auth-session browser.
  const redirect = `${origin}/oauth-callback.html`;
  const startUrl =
    `${getStytchApiBase()}/v1/public/oauth/google/start` +
    `?public_token=${encodeURIComponent(publicToken)}` +
    `&login_redirect_url=${encodeURIComponent(redirect)}` +
    `&signup_redirect_url=${encodeURIComponent(redirect)}` +
    `&provider_prompt=select_account`;

  const { callbackUrl } = await plugin.start({ url: startUrl, callbackScheme: NATIVE_OAUTH_SCHEME });
  if (!callbackUrl) return null;

  const parsed = new URL(callbackUrl);
  const token = parsed.searchParams.get('token') ?? '';
  const stytchTokenType = parsed.searchParams.get('stytch_token_type') ?? 'oauth';
  if (!token) return null;
  return { token, stytchTokenType };
}
